import type { ICreateLocationPaymentMethod } from "@/data/commands/location/CreateLocationPaymentMethod";
import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { PaymentDeviceDao } from "@/data/dao/service/PaymentDeviceDao";
import { PaymentMethodMode, PaymentProvider, PostFinanceContract } from "@/data/model/Enums";
import type { ILocationPaymentMethod } from "@/data/model/LocationPaymentMethod";
import { exceptionTypes } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";



import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";



import { i18n } from "@/portal/Trans";
import { ImagePicker } from "@/portal/formControls/ImagePicker";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { UiNotification } from "@/portal/ui/Notification";


export class LocationPaymentMethodModal extends BaseModal {
    private readonly $form: JQuery;
    private readonly $nameField: JQuery;
    private readonly $orderField: JQuery;
    private readonly $merchantIdField: JQuery;
    private readonly $paymentMethodExternalIdField: JQuery;
    private readonly $additionalSettingsContainer: JQuery;
    private readonly $isCardSchemeRowGroup: JQuery;
    private readonly $isPaymentDeviceRowGroup: JQuery;
    private readonly $additionalSettingsSection: JQuery;
    private readonly $onlinePaymentMethodOptionsSection: JQuery;
    private readonly $externalPaymentIdLabel: JQuery;
    private readonly $allowTipOnlyOnDeviceRowGroup: JQuery;
    private readonly $revenueEffectiveRowGroup: JQuery;

    private readonly logoPicker: ImagePicker<ILocationPaymentMethod>;
    private readonly isEnabledSwitch: Switch;
    private readonly modeSelect: Select<PaymentMethodMode>;
    private readonly providerSelect: Select<PaymentProvider>;
    private readonly postFinanceContractSelect: Select<PostFinanceContract>;

    private readonly $postfinanceContractSection: JQuery;
    private readonly $payTecSection: JQuery;
    private readonly $useTerminalApiRowGroup: JQuery;
    private readonly $apiTokenRowGroup: JQuery;
    private readonly $apiTokenField: JQuery;
    private readonly $integratorIdRowGroup: JQuery;
    private readonly $integratorIdField: JQuery;
    private readonly $pairingCodeField: JQuery;
    private readonly $pairConnectBtn: JQuery;
    private readonly $disconnectBtn: JQuery;

    private readonly isCardSchemeSwitch: Switch;
    private readonly isPaymentDeviceSwitch: Switch;
    private readonly allowTipOnlyOnDeviceSwitch: Switch;
    private readonly revenueEffectiveSwitch: Switch;
    private readonly useTerminalApiSwitch: Switch;

    private $submitBtn?: JQuery;

    private readonly locationId: string;
    private readonly model: ILocationPaymentMethod;

    public onSaved?: { (locationPaymentMethod: ILocationPaymentMethod, wasEdited: boolean) };
    public onRemoved?: { () };

    constructor(locationId: string, model?: ILocationPaymentMethod) {
        super(
            model === undefined
                ? i18n("modal.locationPaymentMethod.addTitle")
                : i18n("modal.locationPaymentMethod.editTitle"),
            "modal-xl",
            true
        );

        this.locationId = locationId;
        this.model = model!;
        this.$element.addClass("service");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.locationPaymentMethod.locationPaymentMethod") +
                "</h3></div></div>"
        );

        // Name
        const nameRowGroup = $('<div class="form-group row"></div>').appendTo(createSection);
        $(`<label class="col-xl-4 col-form-label">${i18n("modal.locationPaymentMethod.name")}</label>`).appendTo(
            nameRowGroup
        );

        const nameGroup = $('<div class="col-xl-6" /></div>').appendTo(nameRowGroup);
        this.$nameField = $('<input type="text" class="form-control" />').appendTo(nameGroup);

        // Is enabled
        const isEnabledGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.isEnabled") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        $('<input data-switch="true" type="checkbox" name="isenabled"/>').appendTo(isEnabledGroup);
        this.isEnabledSwitch = new Switch(this.$form.find('input[name="isenabled"]'));

        // Logo
        const logoGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.logo") +
                '<span class="mandatory">*</span></label><div class="col-lg-9 col-xl-6" id="logo"></div></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");

        this.logoPicker = new ImagePicker<ILocationPaymentMethod>({
            create: undefined as any,
            remove: undefined as any,
            valueAccessor: (m, setter) => {
                if (setter !== undefined) m.logoPath = setter == null ? undefined : setter;

                return m.logoPath;
            },
        }).renderTo(logoGroup);

        // Mode
        const modeGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.mode") +
                '<span class="mandatory">*</span></label><div class="col-lg-9 col-xl-6"><select name="mode" class="form-control"></select></div></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const paymentMethodModeSelect = modeGroup.find('.form-control[name="mode"]');
        Misc.eachEnum(PaymentMethodMode, (i, e) => {
            paymentMethodModeSelect.append(
                '<option value="' + i + '">' + i18n("enums.PaymentMethodMode." + e) + "</option>"
            );
        });

        this.modeSelect = Select.createFromSelect(paymentMethodModeSelect);
        this.modeSelect.onChange = (value) => {
            switch (parseInt(value!)) {
                case PaymentMethodMode.Online:
                    this.$additionalSettingsContainer.show();
                    this.$isCardSchemeRowGroup.show();
                    this.$isPaymentDeviceRowGroup.show();

                    this.revenueEffectiveSwitch.setValue(true);
                    this.revenueEffectiveSwitch.setDisabled(true);
                    break;
                default:
                    this.$additionalSettingsContainer.hide();
                    this.$isCardSchemeRowGroup.hide();
                    this.$isPaymentDeviceRowGroup.hide();
                    this.revenueEffectiveSwitch.setDisabled(false);
                    break;
            }
        };

        // Is card scheme
        this.$isCardSchemeRowGroup = $('<div class="form-group row"></div>').appendTo(createSection);

        $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.isCreditDebitCard") +
                '</label>'
        ).appendTo(this.$isCardSchemeRowGroup);

        const $isCardSchemeContainer = $('<div class="col-xl-6"></div>').appendTo(this.$isCardSchemeRowGroup);
        this.isCardSchemeSwitch = new Switch(
            $('<input data-switch="true" type="checkbox" name="isCardSchemeSwitch"/>').appendTo($isCardSchemeContainer)
        );

        // Payment device
        this.$isPaymentDeviceRowGroup = $('<div class="form-group row"></div>').appendTo(createSection);
        $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.paymentDevice") +
                '</label>'
        ).appendTo(this.$isPaymentDeviceRowGroup);

        const $isPaymentDeviceContainer = $('<div class="col-xl-6"></div>').appendTo(this.$isPaymentDeviceRowGroup);
        this.isPaymentDeviceSwitch = new Switch(
            $('<input data-switch="true" type="checkbox" name="isPaymentDeviceSwitch"/>').appendTo(
                $isPaymentDeviceContainer
            )
        );
        this.isPaymentDeviceSwitch.onChange = (enabled) => {
            if (enabled) {
                this.$allowTipOnlyOnDeviceRowGroup.show();
                this.$externalPaymentIdLabel.html(
                    `${i18n("modal.locationPaymentMethod.deviceId")}<span class="mandatory">*</span>`
                );
            } else {
                this.$allowTipOnlyOnDeviceRowGroup.hide();
                this.$externalPaymentIdLabel.html(
                    `${i18n("modal.locationPaymentMethod.paymentMethodExternalId")}<span class="mandatory">*</span>`
                );
            }

            this.updatePostfinanceContractVisibility();
        };

        // Revenue effective
        this.$revenueEffectiveRowGroup = $('<div class="form-group row"></div>').appendTo(createSection);
        $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.revenueEffective") +
                '<span class="mandatory">*</span></label>'
        ).appendTo(this.$revenueEffectiveRowGroup);

        const $revenueEffectiveContainer = $('<div class="col-xl-6"></div>').appendTo(this.$revenueEffectiveRowGroup);

        this.revenueEffectiveSwitch = new Switch(
            $('<input data-switch="true" type="checkbox" name="revenueEffective"/>').appendTo(
                $revenueEffectiveContainer
            )
        );

        // Order
        const orderGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.order") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");

        this.$orderField = $('<input type="number" class="form-control" />').appendTo(orderGroup);

        this.$additionalSettingsContainer = $(
            '<div class="kt-separator kt-separator--border-dashed kt-separator--portlet-fit kt-separator--space-lg"></div><div class="kt-section" id="additional-settings-section"><div class="kt-section__body"></div></div>'
        ).appendTo(this.$form);

        this.$additionalSettingsSection = this.$additionalSettingsContainer.find(".kt-section__body");
        $(
            '<div class="row"><label class="col-xl-4 col-form-label"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.locationPaymentMethod.additionalSettings") +
                ":</h3></div></div>"
        ).appendTo(this.$additionalSettingsSection);

        // Provider
        const providerGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.provider") +
                '<span class="mandatory">*</span></label><div class="col-lg-9 col-xl-6"><select name="provider" class="form-control"></select></div></div>'
        ).appendTo(this.$additionalSettingsSection);

        const paymentMethodProviderSelect = providerGroup.find('.form-control[name="provider"]');
        Misc.eachEnum(PaymentProvider, (i) => {
            paymentMethodProviderSelect.append('<option value="' + i + '">' + PaymentProvider[i] + "</option>");
        });

        this.providerSelect = Select.createFromSelect(paymentMethodProviderSelect);
        this.providerSelect.onChange = (value) => {
            const selectedProvider = parseInt(value!);
            switch (selectedProvider) {
                case PaymentProvider.Wallee:
                case PaymentProvider.PostFinance:
                case PaymentProvider.PayTec:
                    this.$onlinePaymentMethodOptionsSection.show();
                    break;
                default:
                    this.$onlinePaymentMethodOptionsSection.hide();
                    break;
            }

            this.configureIsPaymentDeviceSwitch();
            this.updatePayTecSectionVisibility();
            this.updatePostfinanceContractVisibility();
            this.updateUseTerminalApiVisibility();
        };

        // Use terminal API
        this.$useTerminalApiRowGroup = $('<div class="form-group row"></div>').appendTo(
            this.$additionalSettingsSection
        );
        $(
            `<label class="col-xl-4 col-form-label">${i18n("modal.locationPaymentMethod.useTerminalApi")}</label>`
        ).appendTo(this.$useTerminalApiRowGroup);

        const $useTerminalApiContainer = $('<div class="col-xl-6"></div>').appendTo(this.$useTerminalApiRowGroup);
        this.useTerminalApiSwitch = new Switch(
            $('<input data-switch="true" type="checkbox" name="useTerminalApiSwitch"/>').appendTo(
                $useTerminalApiContainer
            )
        );
        this.useTerminalApiSwitch.onChange = () => {
            this.updateUseTerminalApiVisibility();
        };

        // Postfinance contract
        this.$postfinanceContractSection = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.contract") +
                '<span class="mandatory">*</span></label><div class="col-lg-9 col-xl-6"><select name="postfinanceContract" class="form-control"></select></div></div>'
        ).appendTo(this.$additionalSettingsSection);

        const postfinanceContractSelect = this.$postfinanceContractSection.find(
            '.form-control[name="postfinanceContract"]'
        );
        Misc.eachEnum(PostFinanceContract, (i, e) => {
            postfinanceContractSelect.append(
                '<option value="' + i + '">' + i18n("enums.PostFinanceContract." + e) + "</option>"
            );
        });

        this.postFinanceContractSelect = Select.createFromSelect(postfinanceContractSelect);

        this.$payTecSection = $('<div class="form-group row"></div>').appendTo(this.$additionalSettingsSection);
        $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.pairingCode") +
                '<span class="mandatory">*</span></label>'
        ).appendTo(this.$payTecSection);

        const pairConnectDeviceSection = $('<div class="col-xl-6" style="display: flex;"/>').appendTo(
            this.$payTecSection
        );

        this.$pairingCodeField = $('<input type="text" class="form-control" />').appendTo(pairConnectDeviceSection);
        this.$pairingCodeField.on("input change", () => {
            this.setPairConnectBtnText(this.$pairingCodeField.val(), this.$merchantIdField.val());
        });

        this.$pairConnectBtn = $(
            '<button type="button" class="btn btn-success ml-4" style="white-space: nowrap;">' +
                i18n("modal.locationPaymentMethod.pairConnect") +
                "</button>"
        )
            .appendTo(pairConnectDeviceSection)
            .click((e) => {
                e.preventDefault();
                this.pairAndConnectDevice();
            });

        this.$disconnectBtn = $(
            '<button type="button" class="btn btn-success ml-4" style="white-space: nowrap;">' +
                i18n("modal.locationPaymentMethod.disconnect") +
                "</button>"
        )
            .appendTo(pairConnectDeviceSection)
            .click((e) => {
                e.preventDefault();
                this.disconnectDevice();
            });

        this.$onlinePaymentMethodOptionsSection = $('<div id="online-payment-method-options-section"></div>').appendTo(
            this.$additionalSettingsSection
        );

        const merchantIdGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.merchantId") +
                '<span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(this.$onlinePaymentMethodOptionsSection)
            .find("> .col-xl-6");
        this.$merchantIdField = $('<input type="text" class="form-control" />').appendTo(merchantIdGroup);
        this.$merchantIdField.on("input change", () => {
            this.setPairConnectBtnText(this.$pairingCodeField.val(), this.$merchantIdField.val());
        });

        // External id
        const paymentMethodExternalIdRowGroup = $('<div class="form-group row"></div>').appendTo(
            this.$onlinePaymentMethodOptionsSection
        );
        this.$externalPaymentIdLabel = $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.paymentMethodExternalId") +
                '<span class="mandatory">*</span></label>'
        ).appendTo(paymentMethodExternalIdRowGroup);

        const paymentMethodExternalIdGroup = $('<div class="col-xl-6" /></div>').appendTo(
            paymentMethodExternalIdRowGroup
        );

        this.$paymentMethodExternalIdField = $('<input type="text" class="form-control" />').appendTo(
            paymentMethodExternalIdGroup
        );

        // Api token
        this.$apiTokenRowGroup = $('<div class="form-group row"></div>').appendTo(
            this.$onlinePaymentMethodOptionsSection
        );
        $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.apiToken") +
                '<span class="mandatory">*</span></label>'
        ).appendTo(this.$apiTokenRowGroup);

        const apiTokenGroup = $('<div class="col-xl-6" /></div>').appendTo(this.$apiTokenRowGroup);
        this.$apiTokenField = $('<input type="password" class="form-control" autocomplete="new-password" />').appendTo(
            apiTokenGroup
        );

        // Integrator id
        this.$integratorIdRowGroup = $('<div class="form-group row"></div>').appendTo(
            this.$onlinePaymentMethodOptionsSection
        );
        $(
            '<label class="col-xl-4 col-form-label">' +
                i18n("modal.locationPaymentMethod.integratorId") +
                '<span class="mandatory">*</span></label>'
        ).appendTo(this.$integratorIdRowGroup);

        const integratorIdGroup = $('<div class="col-xl-6" /></div>').appendTo(this.$integratorIdRowGroup);
        this.$integratorIdField = $('<input type="text" class="form-control" />').appendTo(integratorIdGroup);

        // Allow tip only on device
        this.$allowTipOnlyOnDeviceRowGroup = $('<div class="form-group row"></div>').appendTo(
            this.$additionalSettingsSection
        );
        $(
            `<label class="col-xl-4 col-form-label">${i18n("modal.locationPaymentMethod.allowTipOnlyOnDevice")}</label>`
        ).appendTo(this.$allowTipOnlyOnDeviceRowGroup);

        const allowTipOnlyOnDeviceGroup = $('<div class="col-xl-6" /></div>').appendTo(
            this.$allowTipOnlyOnDeviceRowGroup
        );

        this.allowTipOnlyOnDeviceSwitch = new Switch(
            $('<input data-switch="true" type="checkbox" name="allowTipOnlyOnDeviceSwitch"/>').appendTo(
                allowTipOnlyOnDeviceGroup
            )
        );

        if (this.model != null) {
            this.logoPicker.setValue(this.model);
            this.$nameField.val(this.model.name!);
            this.modeSelect.setValue(model!.mode == null ? undefined : model!.mode);
            this.providerSelect.setValue(model!.provider == null ? undefined : model!.provider);
            this.$merchantIdField.val(model!.merchantId!);
            this.isEnabledSwitch.setValue(model!.isEnabled);
            this.$paymentMethodExternalIdField.val(model!.externalPaymentId!);
            this.$orderField.val(model!.order!);
            this.isCardSchemeSwitch.setValue(model!.isCardScheme);
            this.isPaymentDeviceSwitch.setValue(model!.isPaymentDevice);
            this.postFinanceContractSelect.setValue(
                model!.postFinanceContract ?? PostFinanceContract.BrowserAndDevicePayment
            );
            this.allowTipOnlyOnDeviceSwitch.setValue(model!.allowTipOnlyOnDevice);
            this.revenueEffectiveSwitch.setValue(model!.revenueEffective);
            this.useTerminalApiSwitch.setValue(model!.worldlineConfiguration?.useTerminalApi);
            // the stored token is never sent to the client - leaving the field empty keeps it
            this.$apiTokenField.val("");
            this.$apiTokenField.attr(
                "placeholder",
                model!.worldlineConfiguration?.hasApiToken === true ? "••••••••" : ""
            );
            this.$integratorIdField.val(model!.worldlineConfiguration?.integratorId!);
        } else {
            this.logoPicker.setValue(undefined);
            this.modeSelect.setValue(undefined);
            this.providerSelect.setValue(undefined);
            this.$merchantIdField.val(undefined!);
            this.isEnabledSwitch.setValue(false);
            this.$paymentMethodExternalIdField.val(undefined!);
            this.postFinanceContractSelect.setValue(PostFinanceContract.BrowserAndDevicePayment);
            this.$orderField.val(undefined!);
            this.isCardSchemeSwitch.setValue(false);
            this.isPaymentDeviceSwitch.setValue(false);
            this.allowTipOnlyOnDeviceSwitch.setValue(false);
            this.revenueEffectiveSwitch.setValue(true);
            this.useTerminalApiSwitch.setValue(false);
            this.$apiTokenField.val(undefined!);
            this.$apiTokenField.attr("placeholder", "");
            this.$integratorIdField.val(undefined!);
        }

        this.modeSelect.setDisabled(this.model != null);
        this.providerSelect.setDisabled(this.model != null && !this.model.isProviderEditable);

        this.setPairConnectBtnText(undefined, model?.merchantId);

        if (model == null) {
            this.$additionalSettingsContainer.hide();
            this.$isCardSchemeRowGroup.hide();
            this.$isPaymentDeviceRowGroup.hide();
            this.$payTecSection.hide();
            this.revenueEffectiveSwitch.setDisabled(false);
            this.$allowTipOnlyOnDeviceRowGroup.hide();
        } else {
            this.revenueEffectiveSwitch.setDisabled(true);

            if (model.mode === PaymentMethodMode.Online) {
                this.$additionalSettingsContainer.show();
                this.$isCardSchemeRowGroup.show();

                if (model.provider === PaymentProvider.Stripe) {
                    this.$isPaymentDeviceRowGroup.hide();
                } else {
                    this.$isPaymentDeviceRowGroup.show();
                }

                if (model.provider === PaymentProvider.PayTec) {
                    this.$payTecSection.show();
                }
            } else {
                this.$additionalSettingsContainer.hide();
                this.$isCardSchemeRowGroup.hide();
                this.$isPaymentDeviceRowGroup.hide();
            }

            if (this.model.isPaymentDevice) {
                this.$allowTipOnlyOnDeviceRowGroup.show();
            } else {
                this.$allowTipOnlyOnDeviceRowGroup.hide();
            }

            if (!this.model.isCustom) {
                nameRowGroup.hide();
            }
        }

        if (model == null) {
            this.$onlinePaymentMethodOptionsSection.hide();
        } else {
            if (
                model.provider === PaymentProvider.Wallee ||
                model.provider === PaymentProvider.PayTec ||
                model.provider === PaymentProvider.PostFinance
            ) {
                this.$onlinePaymentMethodOptionsSection.show();
            } else {
                this.$onlinePaymentMethodOptionsSection.hide();
            }
        }

        this.configureIsPaymentDeviceSwitch();
        this.updatePayTecSectionVisibility();
        this.updatePostfinanceContractVisibility();
        this.updateUseTerminalApiVisibility();

        this.insertFooter();

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }
    private setPairConnectBtnText(pairingCode: string | undefined, accessToken: string | undefined) {
        if ((pairingCode == null || pairingCode.length == 0) && accessToken != null && accessToken.length > 0) {
            this.$pairConnectBtn.text(i18n("modal.locationPaymentMethod.connect"));
        } else {
            this.$pairConnectBtn.text(i18n("modal.locationPaymentMethod.pairConnect"));
        }
    }

    private configureIsPaymentDeviceSwitch() {
        const selectedProvider = parseInt(this.providerSelect.getValue() ?? "");

        if (selectedProvider === PaymentProvider.PayTec || selectedProvider === PaymentProvider.Worldline) {
            this.isPaymentDeviceSwitch.setValue(true);
            this.isPaymentDeviceSwitch.setDisabled(true);
        } else if (
            this.model?.predefinedPaymentMethod?.type === "Wallee" ||
            this.model?.predefinedPaymentMethod?.type === "PostFinanceStandard"
        ) {
            // these two standard methods are browser methods. switching one to a payment device
            // moves it into the payment device section, which is not a setup they support
            this.isPaymentDeviceSwitch.setDisabled(true);
        } else {
            this.isPaymentDeviceSwitch.setDisabled(false);
        }
    }

    private updateUseTerminalApiVisibility() {
        const selectedProvider = parseInt(this.providerSelect.getValue() ?? "");

        if (selectedProvider === PaymentProvider.Worldline) {
            this.$useTerminalApiRowGroup.show();

            // merchant id and device id are shared with the other providers, the api token and integrator id are worldline only
            if (this.useTerminalApiSwitch.getValue()) {
                this.$onlinePaymentMethodOptionsSection.show();
                this.$apiTokenRowGroup.show();
                this.$integratorIdRowGroup.show();
            } else {
                this.$onlinePaymentMethodOptionsSection.hide();
                this.$apiTokenRowGroup.hide();
                this.$integratorIdRowGroup.hide();
            }
        } else {
            this.$useTerminalApiRowGroup.hide();
            this.$apiTokenRowGroup.hide();
            this.$integratorIdRowGroup.hide();
        }
    }

    private updatePayTecSectionVisibility() {
        const selectedProvider = parseInt(this.providerSelect.getValue() ?? "");

        if (selectedProvider === PaymentProvider.PayTec) {
            this.$payTecSection.show();
        } else {
            this.$payTecSection.hide();
        }
    }

    private updatePostfinanceContractVisibility() {
        const selectedProvider = parseInt(this.providerSelect.getValue() ?? "");
        const isPaymentDevice = this.isPaymentDeviceSwitch.getValue();

        if (selectedProvider === PaymentProvider.PostFinance && isPaymentDevice) {
            this.$postfinanceContractSection.show();
        } else {
            this.$postfinanceContractSection.hide();
        }
    }

    private insertFooter() {
        if (this.model !== undefined && this.model.isCustom) {
            const deleteBtn = $(
                '<button class="btn btn-clean btn-text-danger btn-small">' + i18n("general.delete") + "</button>"
            )
                .appendTo(this.$footer!)
                .click((e) => {
                    e.preventDefault();
                    deleteBtn
                        .addClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                        .prop("disabled", true);
                    Factory.getInstance<LocationDao>(LocationDao)
                        .removeLocationPaymentMethod(this.locationId, this.model.id!)
                        .then(() => {
                            this.close();
                            if (this.onRemoved !== undefined) this.onRemoved();
                        })
                        .catch((ex) => {
                            logError(ex);
                            deleteBtn
                                .removeClass("kt-spinner kt-spinner--left kt-spinner--sm kt-spinner--primary disabled")
                                .prop("disabled", false);
                            UiNotification.showException(i18n("modal.locationPaymentMethod.failedDelete"));
                        });
                });
        }

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.save") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });
    }

    private pairAndConnectDevice() {
        let hasError = false;
        this.$pairingCodeField.removeClass("is-invalid");

        const pairingCode = this.$pairingCodeField.val();
        const accessToken = this.$merchantIdField.val();
        if ((pairingCode == null || pairingCode.length === 0) && (accessToken == null || accessToken.length === 0)) {
            hasError = true;
            this.$pairingCodeField.addClass("is-invalid");
        }

        if (!hasError) {
            const dao = Factory.getInstance<PaymentDeviceDao>(PaymentDeviceDao);

            this.$pairConnectBtn!.addClass(style.buttonLoaderClass).prop("disabled", true);
            dao.payTecPairAndConnect({
                locationId: this.locationId,
                pairingCode: pairingCode,
                accessToken: accessToken,
            })
                .then((result) => {
                    this.$merchantIdField.val(result.accessToken);
                    this.$paymentMethodExternalIdField.val(result.paymentDeviceId);
                    this.$pairingCodeField.val("");
                    this.setPairConnectBtnText(this.$pairingCodeField.val(), this.$merchantIdField.val());

                    UiNotification.showSuccessAlert(i18n("modal.locationPaymentMethod.deviceConnected"));
                })
                .catch((error) => {
                    if (error instanceof DaoError && error.type === exceptionTypes.payTecConnectFailedException) {
                        UiNotification.showException(i18n("modal.locationPaymentMethod.deviceConnectFailed"));
                    } else {
                        UiNotification.showException(error);
                    }
                })
                .finally(() => {
                    this.$pairConnectBtn!.removeClass(style.buttonLoaderClass).prop("disabled", false);
                });
        }
    }

    private disconnectDevice() {
        let hasError = false;
        this.$pairingCodeField.removeClass("is-invalid");

        const accessToken = this.$merchantIdField.val();
        if (accessToken == null || accessToken.length === 0) {
            hasError = true;
            this.$merchantIdField.addClass("is-invalid");
        }

        if (!hasError) {
            const dao = Factory.getInstance<PaymentDeviceDao>(PaymentDeviceDao);

            this.$disconnectBtn!.addClass(style.buttonLoaderClass).prop("disabled", true);
            dao.payTecDisconnect({ accessToken: accessToken })
                .then(() => {
                    UiNotification.showSuccessAlert(i18n("modal.locationPaymentMethod.deviceDisconnected"));
                })
                .catch((error) => {
                    UiNotification.showException(error);
                })
                .finally(() => {
                    this.$disconnectBtn!.removeClass(style.buttonLoaderClass).prop("disabled", false);
                });
        }
    }

    private readMerchantId(): string {
        return (this.$merchantIdField.val() as string).trim();
    }

    private readExternalPaymentId(): string {
        return (this.$paymentMethodExternalIdField.val() as string).trim();
    }

    private readApiToken(): string {
        return (this.$apiTokenField.val() as string).trim();
    }

    private readIntegratorId(): string {
        return (this.$integratorIdField.val() as string).trim();
    }

    private onSubmit() {
        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        const selectedProvider = this.providerSelect.getValue();
        const selectedMode = this.modeSelect.getValue();
        const order = parseInt(this.$orderField.val());
        const cmd: ICreateLocationPaymentMethod = {
            locationId: this.locationId,
            logo: this.logoPicker.hasPendingDelete === false ? this.logoPicker.pendingFile : undefined,
            name: this.$nameField.val(),
            mode: selectedMode == null ? undefined : (parseInt(this.modeSelect.getValue()!) as any),
            provider: selectedProvider == null ? undefined : parseInt(this.providerSelect.getValue()!),
            merchantId: this.readMerchantId(),
            externalPaymentId: this.readExternalPaymentId(),
            isEnabled: this.isEnabledSwitch.getValue(),
            order: order == null || isNaN(order) ? undefined : order,
            isCardScheme: this.isCardSchemeSwitch.getValue(),
            isPaymentDevice: this.isPaymentDeviceSwitch.getValue(),
            predefinedPaymentMethodId: this.model?.predefinedPaymentMethod?.id,
            allowTipOnlyOnDevice: this.allowTipOnlyOnDeviceSwitch.getValue(),
            revenueEffective: this.revenueEffectiveSwitch.getValue(),
            requireEndUserEmail: true,
            postFinanceContract: parseInt(this.postFinanceContractSelect.getValue()!),
            worldlineUseTerminalApi: this.useTerminalApiSwitch.getValue(),
            worldlineApiToken: this.readApiToken(),
            worldlineIntegratorId: this.readIntegratorId(),
        };

        const isCustom = this.model?.isCustom ?? true;
        // Predefined PM doesn't need name or logo
        if (isCustom) {
            if (cmd.name == null || cmd.name === "") {
                hasError = true;
                this.$nameField.addClass("is-invalid");
            }

            // Check if pm has already a logo or there is a pending logo
            if ((this.model?.logoPath == null && cmd.logo == null) || this.logoPicker.hasPendingDelete) {
                hasError = true;
                this.logoPicker.setHasError(true);
            }
        }

        if (cmd.mode == null || isNaN(cmd.mode)) {
            hasError = true;
            this.modeSelect.setError(true);
        }

        if (cmd.mode === PaymentMethodMode.Online) {
            if (cmd.provider == null || isNaN(cmd.provider)) {
                hasError = true;
                this.providerSelect.setError(true);
            }

            if (cmd.provider === PaymentProvider.Wallee || cmd.provider === PaymentProvider.PostFinance) {
                if (cmd.merchantId == null || cmd.merchantId === "") {
                    hasError = true;
                    this.$merchantIdField.addClass("is-invalid");
                }

                if (cmd.externalPaymentId == null || cmd.externalPaymentId === "") {
                    hasError = true;
                    this.$paymentMethodExternalIdField.addClass("is-invalid");
                }
            }

            if (cmd.provider === PaymentProvider.Worldline && cmd.worldlineUseTerminalApi) {
                if (cmd.merchantId == null || cmd.merchantId === "") {
                    hasError = true;
                    this.$merchantIdField.addClass("is-invalid");
                }

                if (cmd.externalPaymentId == null || cmd.externalPaymentId === "") {
                    hasError = true;
                    this.$paymentMethodExternalIdField.addClass("is-invalid");
                }

                // an empty token keeps the stored one
                if (
                    (cmd.worldlineApiToken == null || cmd.worldlineApiToken === "") &&
                    this.model?.worldlineConfiguration?.hasApiToken !== true
                ) {
                    hasError = true;
                    this.$apiTokenField.addClass("is-invalid");
                }

                if (cmd.worldlineIntegratorId == null || cmd.worldlineIntegratorId === "") {
                    hasError = true;
                    this.$integratorIdField.addClass("is-invalid");
                }
            }
        }

        if (hasError) return;

        const dao = Factory.getInstance<LocationDao>(LocationDao);
        const promise =
            this.model?.id == null
                ? dao.createLocationPaymentMethod(this.locationId, cmd)
                : dao.updateLocationPaymentMethod(this.locationId, this.model.id!, cmd);

        this.$submitBtn!.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        promise
            .then((m) => {
                this.$submitBtn!.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn!.find("> .la").length === 0) this.$submitBtn!.prepend('<i class="la la-save" />');

                this.close();
                if (this.onSaved !== undefined) {
                    this.onSaved(m, this.model != null);
                }
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn!.removeClass(style.buttonLoaderClass).prop("disabled", false);
                if (this.$submitBtn!.find("> .la").length === 0) this.$submitBtn!.prepend('<i class="la la-save" />');

                UiNotification.showException(i18n("modal.locationPaymentMethod.failedEdit"));
            });
    }
}
